/**
 * 단위 체계 (작업지시서 P2-01)
 *
 * ★ 내부 좌표는 **정수 마이크로미터(µm)** 다.
 *
 *   왜 float 가 아닌가 — `StoreCAD_기획서_고도화_제안_v2.md` 2.1절:
 *   오프셋·회전·스냅을 반복하면 float64 에 `1500.0000000000002` 같은 값이 쌓인다.
 *   그러면 "같은 점"인데 스냅이 안 붙고, 치수가 1499.99mm 로 표기된다.
 *   CAD 에서 이건 치명적이다.
 *
 *   안전 범위: Number.MAX_SAFE_INTEGER = 9.007e15 µm ≈ 9,007 km.
 *   마트 도면(최대 수백 m)에는 차고 넘친다.
 *
 *   규칙:
 *     · 저장·연산은 전부 정수 µm
 *     · 화면 표시와 사용자 입력 시점에만 mm 로 환산
 *     · 삼각함수 등 무리수가 끼는 계산은 마지막에 반드시 Math.round()
 */

/** 정수 마이크로미터. 모든 내부 좌표의 단위. */
export type Micron = number;

/** 표시/입력 단위 */
export type DisplayUnit = 'mm' | 'cm' | 'm';

export const UM_PER_MM = 1000;
export const UM_PER_CM = 10_000;
export const UM_PER_M = 1_000_000;

const PER_UNIT: Record<DisplayUnit, number> = {
  mm: UM_PER_MM,
  cm: UM_PER_CM,
  m: UM_PER_M,
};

/** mm → µm (정수로 반올림) */
export function mm(value: number): Micron {
  return Math.round(value * UM_PER_MM);
}

/** m → µm */
export function m(value: number): Micron {
  return Math.round(value * UM_PER_M);
}

/** µm → mm (실수. 표시용) */
export function toMm(v: Micron): number {
  return v / UM_PER_MM;
}

/** µm → 표시 단위 실수 */
export function toDisplay(v: Micron, unit: DisplayUnit): number {
  return v / PER_UNIT[unit];
}

/** 표시 단위 실수 → µm 정수 */
export function fromDisplay(value: number, unit: DisplayUnit): Micron {
  return Math.round(value * PER_UNIT[unit]);
}

/**
 * 화면 표기용 문자열.
 * CAD 사용자는 소수점 자릿수가 흔들리는 걸 싫어하므로 단위별 고정 자릿수를 쓴다.
 */
export function formatLength(v: Micron, unit: DisplayUnit = 'mm'): string {
  const n = toDisplay(v, unit);
  const digits = unit === 'mm' ? 1 : unit === 'cm' ? 2 : 3;
  return `${n.toFixed(digits)}`;
}

/** 면적 µm² → m² */
export function toSquareMeters(areaUm2: number): number {
  return areaUm2 / (UM_PER_M * UM_PER_M);
}

/* -------------------------------------------------------------------------- *
 * 각도 — 내부는 라디안, UI 경계에서만 도(°)
 * -------------------------------------------------------------------------- */

export function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

/** 0 ≤ θ < 2π 로 정규화 */
export function normalizeAngle(rad: number): number {
  const tau = Math.PI * 2;
  const r = rad % tau;
  return r < 0 ? r + tau : r;
}
